import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import ColumnGroupingTable from "../../../table/ColumnGroupingTable.component";

const NiiOutputTable = ({
  niiInputTableData,
  niiInputTablecolumn,
  setNiiOutputTablecolumn,
  setNiiOutputTableData,
  selectedDecimalsIn,
} = {}) => {
  const columns = useMemo(() => {
    return niiInputTablecolumn || [];
  }, [niiInputTablecolumn]);

  const applyNegativeStyles = (rowData, monthColumns) => {
    const dynamicStyles = {};
    let hasNegative = false;

    monthColumns.forEach((month) => {
      const value = rowData[month];

      if (typeof value === "number" && (value < 0 || Object.is(value, -0))) {
        dynamicStyles[month] = { color: "red" };
        hasNegative = true;
      }
    });

    return hasNegative ? { dynamicCellStyles: dynamicStyles } : {};
  };

  const datas = useMemo(() => {
    if (
      !niiInputTableData ||
      niiInputTableData.length === 0 ||
      !columns ||
      columns.length === 0
    ) {
      return [];
    }

    const outputData = [];
    const monthColumns = columns.slice(2).map((col) => col.id);

    const totalAssetsRunOff = {};
    const totalLiabilitiesRunOff = {};
    const monthlyGap = {};
    const remainingMonthData = {
      type: "Remaining Month",
      cashflow: "Remaining Month",
      rowSpan: 1,
    };
    const niiChangeData = {
      type: "Change in NII caused by re-pricing",
      cashflow: "Change in NII caused by re-pricing",
      rowSpan: 1,
    };

    let currentRemainingMonthCount = 12;
    let currentMonthIndex = 0;

    monthColumns.forEach((month) => {
      totalAssetsRunOff[month] = 0;
      totalLiabilitiesRunOff[month] = 0;
      monthlyGap[month] = 0;

      if (currentMonthIndex === 0) {
        remainingMonthData[month] = "";
      } else {
        remainingMonthData[month] = currentRemainingMonthCount;
        currentRemainingMonthCount -= 1;
      }
      currentMonthIndex += 1;
    });

    let currentGroupRows = [];
    let currentType = null;

    const pushGroupWithTotal = (isAssetGroup) => {
      if (currentGroupRows.length === 0) return;

      const totalRowStylesSource = isAssetGroup
        ? totalAssetsRunOff
        : totalLiabilitiesRunOff;

      const totalRowDynamicStyles = applyNegativeStyles(
        totalRowStylesSource,
        monthColumns,
      );

      const totalRow = isAssetGroup
        ? {
            type: "Run-off of Assets (Month over Month)",
            cashflow: "Total Run-off (A)",
            rowSpan: 0,
            cellStyles: {
              fontWeight: "bold",
              backgroundColor: "#f0f0f0",
              borderTop: "1px solid #000000",
              borderBottom: "1px solid #000000",
            },
            ...totalAssetsRunOff,
            ...totalRowDynamicStyles,
          }
        : {
            type: "Run-off of Liabilities (Month over Month)",
            cashflow: "Total Run-off (B)",
            rowSpan: 0,
            cellStyles: {
              fontWeight: "bold",
              backgroundColor: "#f0f0f0",
              borderTop: "1px solid #000000",
              borderBottom: "1px solid #000000",
            },
            ...totalLiabilitiesRunOff,
            ...totalRowDynamicStyles,
          };

      currentGroupRows[0].rowSpan = currentGroupRows.length + 1;

      outputData.push(...currentGroupRows);
      outputData.push(totalRow);

      currentGroupRows = [];
    };

    for (const inputRow of niiInputTableData) {
      const inputRowType = inputRow.type;

      if (currentType === "Assets" && inputRowType === "Liabilities") {
        pushGroupWithTotal(true);
        currentType = "Liabilities";
      } else if (currentType === null) {
        currentType = inputRowType;
      }

      const outputRow = {
        type: `Run-off of ${inputRowType} (Month over Month)`,
        cashflow: inputRow.cashflow,
        rowSpan: inputRow.rowSpan,
      };

      const calculatedValues = {};

      for (let i = 0; i < monthColumns.length; i++) {
        const currentMonth = monthColumns[i];
        let runOff = 0;

        if (i === 0) {
          runOff = 0;
        } else {
          const previousMonth = monthColumns[i - 1];
          const prevValue = inputRow[previousMonth] || 0;
          const currValue = inputRow[currentMonth] || 0;
          const formattedRunOff = Object.is(prevValue - currValue, -0)
            ? -0
            : prevValue - currValue;
          runOff = parseFloat(formattedRunOff?.toFixed(selectedDecimalsIn));
        }

        calculatedValues[currentMonth] = runOff;

        if (inputRowType === "Assets") {
          totalAssetsRunOff[currentMonth] = parseFloat(
            (totalAssetsRunOff[currentMonth] + runOff)?.toFixed(
              selectedDecimalsIn,
            ),
          );
        } else if (inputRowType === "Liabilities") {
          totalLiabilitiesRunOff[currentMonth] = parseFloat(
            (totalLiabilitiesRunOff[currentMonth] + runOff)?.toFixed(
              selectedDecimalsIn,
            ),
          );
        }
      }

      const individualRowDynamicStyles = applyNegativeStyles(
        calculatedValues,
        monthColumns,
      );

      currentGroupRows.push({
        ...outputRow,
        ...calculatedValues,
        ...individualRowDynamicStyles,
      });
    }

    if (currentType === "Liabilities") {
      pushGroupWithTotal(false);
    }

    const monthlyGapDynamicStyles = {};

    monthColumns.forEach((month) => {
      const gapValue = totalAssetsRunOff[month] - totalLiabilitiesRunOff[month];
      monthlyGap[month] = parseFloat(gapValue?.toFixed(selectedDecimalsIn));
      if (gapValue < 0) {
        monthlyGapDynamicStyles[month] = { color: "red" };
      }
    });

    outputData.push({
      type: "Monthly GAP",
      cashflow: "Monthly GAP  ( A - B )",
      rowSpan: 1,
      ...monthlyGap,
      dynamicCellStyles: monthlyGapDynamicStyles,
    });

    const niiChangeDynamicStyles = {};
    const decimals =
      Number(selectedDecimalsIn) >= 0 ? Number(selectedDecimalsIn) : 0;

    monthColumns.forEach((month) => {
      const gapValue = monthlyGap[month] || 0;
      const remainingMonths = remainingMonthData[month] || 0;

      let niiChange = 0;
      if (remainingMonths !== "") {
        niiChange = (gapValue * 0.01 * remainingMonths) / 12;
        niiChange = Object.is(niiChange, -0) ? -0 : niiChange;
      }
      const scaledValue = Number(niiChange);
      const formattedValue = parseFloat(scaledValue.toFixed(decimals));
      niiChangeData[month] = formattedValue;
      if (niiChange < 0 || Object.is(niiChange, -0)) {
        niiChangeDynamicStyles[month] = { color: "red" };
      }
    });

    outputData.push({
      ...niiChangeData,
      cellStyles: {
        fontWeight: "bold",
        borderTop: "1px solid #000000",
        borderBottom: "1px solid #000000",
      },
      dynamicCellStyles: niiChangeDynamicStyles,
    });

    outputData.push({
      ...remainingMonthData,
    });

    return outputData;
  }, [niiInputTableData, columns, selectedDecimalsIn]);

  useEffect(() => {
    if (datas) {
      setNiiOutputTablecolumn(columns);
      setNiiOutputTableData(datas);
    }
  }, [columns, datas, setNiiOutputTablecolumn, setNiiOutputTableData]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        minHeight: "250px",
      }}
    >
      <Grid item xs={12} md={12} lg={12}>
        <ColumnGroupingTable
          columns={columns}
          datas={datas}
          loading={false}
          disablePagination={true}
          applyThousandsSeparator={true}
          freezeParentColumnCount={2}
          tableHeight={550}
          rowSpanKey="type"
        />
      </Grid>
    </Grid>
  );
};

export default NiiOutputTable;
